import { useFormContext } from 'react-hook-form';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './TextEditorModal.scss';
import { useRef } from 'react';
import { FormLabel } from '@mui/material';
import { PrimaryButton, SecondaryButton } from '@shape-ds/components';
import { useTranslation } from 'react-i18next';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CustomToolbar, { formats } from 'components/TextEditor/CustomToolbar/CustomToolbar';

export type TextEditorModalProps = {
  name: string;
  placeholder: string;
  caseName: string;
  label?: string;
  isDisabled?: boolean;
  'data-testid'?: string;
  isRequired?: boolean;
  closeModal: () => void;
  onSubmit: () => void;
};

export const TextEditorModal = ({
  name,
  placeholder,
  caseName,
  label,
  isDisabled,
  'data-testid': dataTestId,
  isRequired,
  closeModal,
  onSubmit,
}: TextEditorModalProps) => {
  const { t } = useTranslation();
  const quillRef = useRef<ReactQuill>(null);
  const { formState, setValue, watch, handleSubmit } = useFormContext();
  const { isSubmitSuccessful } = formState;

  const fieldValue = watch(name) ?? '';

  const handleSave = (event: React.MouseEvent) => {
    event.preventDefault();
    handleSubmit(() => {
      onSubmit();
    })();
  };
  const toolbarId = `custom-toolbar-${name.replaceAll('.', '-')}-modal`;
  return (
    <>
      <div className="modal-text-editor-content">
        <p className="text-editor-title">{caseName}</p>
        <FormLabel className="text-editor-with-label" required={isRequired}>
          {label}
        </FormLabel>
        <div className="modal-text-editor-container">
          <ReactQuill
            id="react-quill"
            ref={quillRef}
            theme="snow"
            value={fieldValue ?? ''}
            onChange={(value) => {
              return setValue(name, value, { shouldDirty: true, shouldTouch: true });
            }}
            placeholder={placeholder}
            modules={{
              toolbar: {
                container: `#${toolbarId}`,
              },
              resize: {
                locale: {},
              },
              imageDropAndPaste: {},
            }}
            formats={formats}
            readOnly={isDisabled}
            data-testid={dataTestId}
          />
          <CustomToolbar name={name} placeholder={placeholder} id={toolbarId} isModal />
        </div>
      </div>
      <div className="modal-text-editor-footer">
        <SecondaryButton onClick={closeModal}>
          {t('components:statusSelectWithButton.label.cancel')}
        </SecondaryButton>
        <PrimaryButton
          type="submit"
          onClick={handleSave}
          className="text-editor-save-button"
          disabled={isDisabled}
        >
          <CheckCircleOutlineOutlinedIcon className="status-select-button-icon" />
          {isSubmitSuccessful
            ? t('components:statusSelectWithButton.label.saved')
            : t('components:statusSelectWithButton.label.saveDescription')}
        </PrimaryButton>
      </div>
    </>
  );
};
